import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { notificationsService, NotificationItem } from '../services/notificationsService';
import { useAuth } from './AuthContext';

interface NotificationsContextValue {
  notifications: NotificationItem[];
  loading: boolean;
  unreadCount: number;
  error: string | null;
  refresh: () => Promise<void>;
  markViewed: (notificationIds: number[]) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

type NotificationsProviderProps = { children: ReactNode };

export const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const syncUnreadCount = useCallback((items: NotificationItem[]) => {
    setUnreadCount(items.filter((item) => !item.viewed_at).length);
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await notificationsService.list({ page: 1, perPage: 50 });
      if (!response.success) {
        throw new Error(response.error || 'Failed to load notifications');
      }
      const items = response.data.notifications ?? [];
      setNotifications(items);
      syncUnreadCount(items);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load notifications');
      console.error('Failed to load notifications', error);
    } finally {
      setLoading(false);
    }
  }, [syncUnreadCount, user]);

  const markViewed = useCallback(
    async (notificationIds: number[]) => {
      if (!notificationIds.length) {
        return;
      }

      const idsSet = new Set(notificationIds);

      try {
        await Promise.all(notificationIds.map((id) => notificationsService.markViewed(id)));
        setNotifications((prev) => {
          const next = prev.map((item) =>
            idsSet.has(item.id) ? { ...item, viewed_at: item.viewed_at ?? new Date().toISOString() } : item
          );
          syncUnreadCount(next);
          return next;
        });
      } catch (error) {
        console.error('Failed to mark notifications as viewed', error);
      }
    },
    [syncUnreadCount]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ notifications, loading, unreadCount, error, refresh, markViewed }),
    [notifications, loading, unreadCount, error, refresh, markViewed]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
